// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2025 Vic Zhu <viczhu2014@gmail.com>

import { useEffect, useRef, useState } from 'react'
import { useDataProvider } from 'react-admin'
import { Metric } from '../types'

interface UseFetchAllMetricsOptions {
  deviceId?: string | number
  sort?: { field: string; order: 'ASC' | 'DESC' }
  enabled?: boolean
}

interface FetchAllMetricsResult {
  data: Metric[]
  total: number
  isLoading: boolean
  error: any
}

const PAGE_SIZE = 250 // Match backend maxLimit

/**
 * Hook to fetch ALL metrics for a device by paginating through all pages.
 * PayloadCMS has a maxLimit (typically 250) that caps single requests,
 * so this hook makes multiple requests to get all data.
 */
export function useFetchAllMetrics({
  deviceId,
  sort = { field: 'timestamp', order: 'ASC' },
  enabled = true,
}: UseFetchAllMetricsOptions): FetchAllMetricsResult {
  const dataProvider = useDataProvider()
  const [data, setData] = useState<Metric[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<any>(null)

  // Track the current deviceId to avoid stale data
  const currentDeviceIdRef = useRef(deviceId)

  useEffect(() => {
    if (!enabled || !deviceId) {
      setData([])
      setTotal(0)
      return
    }

    currentDeviceIdRef.current = deviceId

    const fetchAllPages = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const allMetrics: Metric[] = []
        let page = 1
        let hasMore = true
        let totalDocs = 0

        while (hasMore) {
          // Check if deviceId changed during fetch
          if (currentDeviceIdRef.current !== deviceId) {
            console.log('[useFetchAllMetrics] deviceId changed, aborting fetch')
            return
          }

          // ALWAYS query newest-first, regardless of the caller's requested
          // sort. The page loop below stops at 100 pages (25,000 rows) and the
          // /metrics endpoint clamps limits server-side; with an ASC query
          // those caps retain the OLDEST rows and silently drop the newest —
          // freezing trend charts on long-history devices (same bug class as
          // useDeviceMetrics, fixed in 6a903029). DESC makes every cap keep the
          // most recent rows; the client-side sort below restores the caller's
          // requested order, so the returned contract is unchanged.
          const response = await dataProvider.getList<Metric>('metrics', {
            // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
            meta: { params: { depth: 0 } },
            filter: { device: deviceId },
            sort: { field: sort.field, order: 'DESC' },
            pagination: { page, perPage: PAGE_SIZE },
          })

          allMetrics.push(...response.data)
          totalDocs = response.total ?? 0

          console.log(
            `[useFetchAllMetrics] Page ${page}: fetched ${response.data.length} metrics, total so far: ${allMetrics.length}/${totalDocs}`,
          )

          // Check if there are more pages
          hasMore = allMetrics.length < totalDocs && response.data.length === PAGE_SIZE
          page++

          // Safety limit to prevent infinite loops
          if (page > 100) {
            console.warn('[useFetchAllMetrics] Reached page limit (100), stopping fetch')
            break
          }
        }

        // Dedupe by id: under a DESC fetch, a reading inserted between page
        // requests shifts page boundaries, so a row can repeat across pages.
        const seenIds = new Set<Metric['id']>()
        const uniqueMetrics = allMetrics.filter((m) => {
          if (seenIds.has(m.id)) return false
          seenIds.add(m.id)
          return true
        })

        // Sort all metrics by timestamp in the caller's requested order
        const sortedMetrics = uniqueMetrics.sort((a, b) => {
          if (sort.order === 'ASC') {
            return a.timestamp - b.timestamp
          }
          return b.timestamp - a.timestamp
        })

        // Only update state if deviceId hasn't changed
        if (currentDeviceIdRef.current === deviceId) {
          setData(sortedMetrics)
          setTotal(totalDocs)
          console.log(
            `[useFetchAllMetrics] Complete: ${sortedMetrics.length} metrics loaded for device ${deviceId}`,
          )
        }
      } catch (err) {
        console.error('[useFetchAllMetrics] Error fetching metrics:', err)
        if (currentDeviceIdRef.current === deviceId) {
          setError(err)
        }
      } finally {
        if (currentDeviceIdRef.current === deviceId) {
          setIsLoading(false)
        }
      }
    }

    fetchAllPages()
  }, [deviceId, sort.field, sort.order, enabled, dataProvider])

  return { data, total, isLoading, error }
}
